import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notify, cancel } from '../../helpers/toastify';
import { useMutation } from '@apollo/client';
import { UPDATE_PREFERENCES, ME } from '@bloomays-lib/adapter.api-bloomer';
import { useTranslation } from 'react-i18next';
import {
  Button,
  LoaderSkeleton,
  TypeInput,
  ValidationTextField,
  ActionButton,
  Title,
  TitlePart as RawTitlePart,
} from '@bloomays-lib/ui.shared';
import { styled } from '@mui/material/styles';
import { BrowserBloomer, NotificationCanal } from '@bloomays-lib/types.shared';
import { errorLogger } from '../../helpers/error';
import { useUserStore } from '../../stores';

const BloomerSettings = () => {
  const { t } = useTranslation(['notify']);
  const [uploading, setUploading] = useState(false);
  const user = useUserStore((state) => state.user);
  const isLoading = useUserStore((state) => state.userLoading);
  const navigate = useNavigate();
  const bloomer = user?.bloomer;

  const [settings, setSettings] = useState(bloomer);
  const [updatePreferences] = useMutation(UPDATE_PREFERENCES, {
    refetchQueries: [
      {
        query: ME,
      },
    ],
  });

  const communicationTypeOpts = [
    { label: 'Pas de notification', value: NotificationCanal.doNotDisturbed },
    { label: 'SMS', value: NotificationCanal.sms },
    { label: 'Email', value: NotificationCanal.email },
    { label: "Whats'app", value: NotificationCanal.whatsapp },
  ];

  const upsertData = async (bloomer: BrowserBloomer) => {
    try {
      notify('info', t('preferencesLoading'), null, {
        hideProgressBar: false,
        autoClose: 10000,
      });
      const upload = await updatePreferences({
        variables: {
          preferences: {
            communicationType: bloomer?.communicationType,
          },
        },
      });
      cancel();
      notify('success', t('preferencesSuccesChange'), null, {
        hideProgressBar: true,
        autoClose: 2000,
      });
      return upload.data.updatePreferences;
    } catch (error: any) {
      cancel();
      notify('error', t('preferencesErrorChanged'), error);
      errorLogger(error, {
        extraInfos: 'extraErrorInfos',
      });
    }
  };

  const onSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!settings || settings?.communicationType === bloomer?.communicationType) {
      return notify('info', t('preferencesHasNoChange'));
    }
    setUploading(true);
    const preferences = await upsertData(settings);
    if (preferences) {
      setUploading(false);
      return true;
    }
  };

  return (
    <BloomerDashboardContainer>
      <Title textTitle="Réglages ⚙️" />
      {isLoading || !bloomer ? (
        ((<LoaderSkeleton height={200} width={500} />) as React.ReactNode)
      ) : (
        <div>
          <FormContainer>
            <ActionButton
              actionType="Edit"
              title={'Éditer mes infos persos'}
              onClick={() => navigate('/me/informations')}
            />
          </FormContainer>
          <TitlePart textTitle={'Mes préférences'} />
          <StyledForm>
            <ValidationTextField
              name="favoriteCommunication"
              label="Mode de communication favori"
              type={TypeInput.select}
              defaultOption={false}
              value={settings?.communicationType || bloomer?.communicationType}
              required={true}
              options={communicationTypeOpts}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                e.preventDefault();
                setSettings({
                  ...bloomer,
                  communicationType: e.target.value as NotificationCanal,
                });
              }}
            />
            <Button
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => onSubmit(e)}
              none={'none'}
              textButton={uploading ? "En cours d'envoi" : 'Sauvegarder'}
              disable={uploading}
            />
          </StyledForm>
        </div>
      )}
    </BloomerDashboardContainer>
  ) as React.ReactNode;
};

export { BloomerSettings };
export default BloomerSettings;

const BloomerDashboardContainer = styled('div')(({ theme }) => ({
  width: '50%',
  margin: '2em auto',
  [theme.breakpoints.down('md')]: {
    width: '95%',
    margin: '2em auto',
  },
}));

const FormContainer = styled('div')(() => ({
  display: 'flex',
  flexFlow: 'row nowrap',
  justifyContent: 'flex-start',
  margin: '0.5em auto',
}));

const StyledForm = styled('form')(() => ({
  display: 'flex',
  flexFlow: 'column nowrap',
  justifyContent: 'center',
  borderRadius: '10px',
  margin: '1.5em auto',
  padding: '1em',
  boxShadow: '2px 2px 20px lightgrey',
}));

const TitlePart = styled(RawTitlePart)(({ theme }) => ({
  textAlign: 'left',
  marginTop: '1em',
}));
